<template>
  <div class="powerbi-report-wrapper">
    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-center align-center" :style="containerStyle">
      <v-progress-circular indeterminate :size="50" :width="4" color="brandCyan" />
    </div>

    <!-- Error state -->
    <v-alert v-else-if="error" type="error" class="ma-4">
      {{ error }}
    </v-alert>

    <!-- Token expired overlay -->
    <div
      v-if="tokenExpired && !loading"
      class="token-expired-overlay d-flex flex-column justify-center align-center"
    >
      <v-icon x-large color="white" class="mb-4">mdi-lock-clock</v-icon>
      <span class="white--text text-h6 mb-4">Your session has expired</span>
      <Button color="brandPrimary" @click="refreshToken" :loading="refreshing">
        Refresh Report
      </Button>
    </div>

    <!-- Power BI embed container -->
    <div ref="embedContainer" class="powerbi-embed-container" :style="containerStyle"></div>
  </div>
</template>

<script>
import * as pbi from "powerbi-client";
import PowerBIReportService from "@/services/PowerBIReportService.js";
import Button from "@/components/generic/Button.vue";
const powerbiService = new pbi.service.Service(
  pbi.factories.hpmFactory,
  pbi.factories.wpmpFactory,
  pbi.factories.routerFactory
);

// Space the Reporting page keeps below the report: the card's pa-6 (24px) and
// the page's 10px bottom padding (.main-content-top in App.vue).
const PAGE_BOTTOM_PADDING = 34;
// Below this, the page scrolls rather than squeeze the report out of legibility.
const MIN_REPORT_HEIGHT = 400;

export default {
  name: "PowerBIReport",
  props: {
    config: {
      type: Object,
      required: true,
    },
    width: {
      type: [Number, String],
      default: "100%",
    },
    height: {
      type: [Number, String],
      default: "100%",
    },
  },
  components: {
    Button,
  },
  data() {
    return {
      loading: true,
      error: null,
      tokenExpired: false,
      refreshing: false,
      report: null,
      embedData: null,
      maxHeight: null,
    };
  },
  computed: {
    containerStyle() {
      const w = typeof this.width === "number" ? `${this.width}px` : this.width;
      const h = typeof this.height === "number" ? `${this.height}px` : this.height;
      const style = { width: w, height: h };
      if (this.maxHeight !== null) {
        style.maxHeight = `${this.maxHeight}px`;
      }
      return style;
    },
  },
  async mounted() {
    this.fitToViewport();
    window.addEventListener("resize", this.fitToViewport);
    await this.loadReport();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.fitToViewport);
    if (this.$refs.embedContainer) {
      powerbiService.reset(this.$refs.embedContainer);
    }
  },
  methods: {
    // Caps the report at the window space below its top edge, so a tall report
    // does not give the whole page a scroll bar.
    fitToViewport() {
      const top = this.$el.getBoundingClientRect().top + window.scrollY;
      this.maxHeight = Math.max(window.innerHeight - top - PAGE_BOTTOM_PADDING, MIN_REPORT_HEIGHT);
    },

    async loadReport() {
      this.loading = true;
      this.error = null;
      this.tokenExpired = false;

      try {
        const configId = this.config.powerbiReportConfigId;
        const data = await PowerBIReportService.getEmbedToken(configId, this.config.clientId);

        if (data instanceof Error || !data.token) {
          this.error = "Failed to load report. Please try again.";
          this.loading = false;
          return;
        }

        this.embedData = data;
        this.embedReport(data);
      } catch (err) {
        this.error = "Failed to load report. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    embedReport(data) {
      const container = this.$refs.embedContainer;
      if (!container) return;

      const embedConfig = {
        type: "report",
        tokenType: pbi.models.TokenType.Embed,
        accessToken: data.token,
        embedUrl: data.embedUrl,
        id: data.reportId,
        permissions: pbi.models.Permissions.Read,
        settings: {
          panes: {
            filters: { visible: false },
            pageNavigation: { visible: true },
          },
          bars: {
            statusBar: { visible: false },
          },
        },
      };

      this.report = powerbiService.embed(container, embedConfig);

      // Apply filters if any were resolved
      if (data.filters && data.filters.length > 0) {
        this.report.on("loaded", () => {
          this.report.setFilters(data.filters);
        });
      }

      // Handle token expiry via the error event
      // (the "tokenExpired" event name is not supported in this SDK version)

      // Handle errors from the SDK
      this.report.on("error", (event) => {
        const errorDetail = event?.detail?.message || "";
        console.log("Power BI SDK error:", errorDetail);
        if (
          errorDetail.toLowerCase().includes("token") &&
          errorDetail.toLowerCase().includes("expir")
        ) {
          this.tokenExpired = true;
        }
      });
    },

    async refreshToken() {
      this.refreshing = true;
      try {
        const configId = this.config.powerbiReportConfigId;
        const data = await PowerBIReportService.getEmbedToken(configId, this.config.clientId);

        if (data instanceof Error || !data.token) {
          this.error = "Failed to refresh report session.";
          return;
        }

        this.embedData = data;
        this.tokenExpired = false;

        if (this.report) {
          await this.report.setAccessToken(data.token);
        }
      } catch (err) {
        this.error = "Failed to refresh report session.";
      } finally {
        this.refreshing = false;
      }
    },
  },
};
</script>

<style scoped>
.powerbi-report-wrapper {
  position: relative;
}

.powerbi-embed-container {
  border: none;
}

/* powerbi-client adds the iframe at run time, so it has no scoped attribute and
   only a deep selector reaches it. Block display drops the inline gap below it,
   which would push the page past the viewport by a few pixels. */
.powerbi-embed-container >>> iframe {
  border: none;
  display: block;
}

.token-expired-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10;
}
</style>
